import {ChangeDetectionStrategy, Component, Inject, OnInit, signal} from '@angular/core';
import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {debounceTime, distinctUntilChanged, first, map, shareReplay, startWith, switchMap, tap} from 'rxjs/operators';
import {FormBuilder, Validators} from '@angular/forms';
import {Clipboard} from '@angular/cdk/clipboard';
import {patternValidator} from '../../../core/pure-utils/validators';
import {environment} from 'projects/dashboard/src/environments/environment';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {AffiliateMerchantDiscount, AffiliateWebsite} from '../../../core/dtos/api';
import {DashboardApiService} from '../../../core/services/api/dashboard-api.service';
import {AffiliateApiService} from '../../../core/services/api/affiliate-api.service';
import {stripHttpWWWFromUrl} from '../../../core/pure-utils/string-utils';
import {outPlaceSort} from '../../../core/pure-utils/array-utils';
import {UtilitiesApiService} from '../../../core/services/api/utilities-api.service';
import {SnackBarService} from '../../../core-services/snack-bar/snack-bar.service';

@Component({
    templateUrl: './get-discount-dialog.component.html',
    styleUrls: ['./get-discount-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GetDiscountDialogComponent implements OnInit {
  readonly ctcValidPattern = /^[A-Za-z\d\.| \-\_~]*$/;

  constructor(@Inject(MAT_DIALOG_DATA) public data: {affiliateMerchantDiscount: AffiliateMerchantDiscount}, private dashboardApiService: DashboardApiService,
              public fb: FormBuilder, private clipboard: Clipboard, private affiliateApiService: AffiliateApiService,
              private utilitiesApiService: UtilitiesApiService, private snackBarService: SnackBarService) {}

  isTrackingLinkLoading = signal(false);

  ngOnInit$ = new ReplaySubject<void>(1);

  affiliateWebsiteControl = this.fb.control({} as AffiliateWebsite);
  urlShorteningControl = this.fb.control(false);
  customTrackingCodeControl = this.fb.control('', [Validators.maxLength(64),
    patternValidator(this.ctcValidPattern, 'Must be alphanumeric, a period, space, hyphen, pipe, tilde, or underscore.')]);
  debouncedCustomTrackingCode$ = this.customTrackingCodeControl.valueChanges.pipe(
    debounceTime(1000),
    distinctUntilChanged());

  affiliateWebsites$ = this.dashboardApiService.currentAccount$.pipe(
    switchMap(currentAccount => this.affiliateApiService.getAffiliateWebsites(currentAccount.entity_id, true)),
    map(affiliateWebsites => {
      let withStrippedUrl =
        affiliateWebsites.map(x =>
          ({...x, affiliateWebsiteUrlStripped: stripHttpWWWFromUrl(x.affiliate_website_url)} as AffiliateWebsite & {affiliateWebsiteUrlStripped: string}));
      return outPlaceSort(withStrippedUrl, x => x.affiliateWebsiteUrlStripped);
    }),
    tap(affiliateWebsites => {
      this.affiliateWebsiteControl.setValue(affiliateWebsites.find(x => x.is_default));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  trackingLink$ = combineLatest([
    this.affiliateWebsiteControl.valueChanges,
    <Observable<'none' | 'avantlink'>> this.urlShorteningControl.valueChanges.pipe(
      startWith(this.urlShorteningControl.value),
      map(urlShortenerControlValue => urlShortenerControlValue ? 'avantlink' : 'none')
    ),
    this.debouncedCustomTrackingCode$.pipe(startWith(this.customTrackingCodeControl.value))]).pipe(
      switchMap(([affiliateWebsite, urlShortener, customTrackingCode]) => {
        let affiliateLinkUrl
          // eslint-disable-next-line max-len
          = `${environment.classicUrl}/click.php?tt=dl&merchant_id=${this.data.affiliateMerchantDiscount.merchant.merchant_id}&discount_id=${this.data.affiliateMerchantDiscount.discount_id}&website_id=${affiliateWebsite.affiliate_website_id}`;
        if (customTrackingCode) {
          affiliateLinkUrl += `&ctc=${customTrackingCode}`;
        }
        this.isTrackingLinkLoading.set(true);
        if (urlShortener === 'none') {
          return of(affiliateLinkUrl);
        }
        return this.utilitiesApiService.shortenUrl([{long_url: affiliateLinkUrl, service: 'avantlink'}]).pipe(map(shortenedUrls => shortenedUrls[0].short_url));
      }),
      tap(x => {
        this.isTrackingLinkLoading.set(false);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
  );

  touchCtcForm$Sub = this.ngOnInit$.pipe(
    first()
  ).subscribe(() => this.customTrackingCodeControl.markAsTouched());

  onCopyClick(trackingUrl: string) {
    this.clipboard.copy(trackingUrl);
    this.snackBarService.openSnackBar('Link Copied');
  }

  onSocialShareClick(social: 'twitter' | 'facebook') {
    this.trackingLink$.pipe(first()).subscribe(trackingUrl => {
        if (social === 'facebook') {
          this.openWindow(`https://www.facebook.com/dialog/share?app_id=517057685622980&display=popup&href=${trackingUrl}`, 'facebookShare');
        } else if (social === 'twitter'){
          this.openWindow(`https://twitter.com/intent/tweet?text=%20&url=${trackingUrl}`, 'twitterIntent');
        }
      });
  }

  public openWindow(openUrl, target) {
    // size and center window
    let width = 550;
    let height = 420;
    let left = Math.round((screen.width / 2) - (width / 2)) + window.screenX;
    let top = 0;
    if (screen.height > height) {
      top = Math.round((screen.height / 2) - (height / 2)) + window.screenY;
    }

    window.open(openUrl, target,
      `width=${width},height=${height},scrollbars=yes,resizable=yes,toolbar=no,location=yes,left=${left},top=${top}`);
  }

  ngOnInit() {
    this.ngOnInit$.next(undefined);
    this.ngOnInit$.complete();
  }
}
